import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api_services';
import auth_service from '../services/auth_service';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState(new Set());
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(null); // null initially to indicate loading
  const [customerId, setCustomerId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check login status
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const isAuth = auth_service.isAuthenticated();
        if (isAuth) {
          const userData = auth_service.getAccessTokenData();
          if (userData && userData.id) {
            setIsLoggedIn(true);
            setCustomerId(userData.id);
          } else {
            setIsLoggedIn(false);
            setCustomerId(null);
          }
        } else {
          setIsLoggedIn(false);
          setCustomerId(null);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setIsLoggedIn(false);
        setCustomerId(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Load wishlist data
  const loadWishlistData = async () => {
    try {
      if (!isLoggedIn || !customerId) {
        // Load from localStorage for non-logged-in users
        const localWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        // Normalize product IDs to ensure consistency
        const wishlistSet = new Set(localWishlist.map(item => String(item.product_id)));
        setWishlistItems(wishlistSet);
        setWishlistProducts(localWishlist);
        setWishlistCount(localWishlist.length);
        console.log('Loaded wishlist from localStorage:', localWishlist.length, 'items');
      } else {
        // Load from API for logged-in users
        console.log('Loading wishlist from API for customer:', customerId);
        const response = await apiService.fetchWishlistProductsById(customerId);
        if (response && response.data) {
          // Normalize product IDs to ensure consistency
          const wishlistSet = new Set(response.data.map(item => String(item.product_id)));
          setWishlistItems(wishlistSet);
          setWishlistProducts(response.data);
          setWishlistCount(response.data.length);
          console.log('Loaded wishlist from API:', response.data.length, 'items');
          console.log('API wishlist IDs:', Array.from(wishlistSet));
        } else {
          // If API fails, fallback to empty state
          setWishlistItems(new Set());
          setWishlistProducts([]);
          setWishlistCount(0);
        }
      }
    } catch (error) {
      console.error('Error loading wishlist data:', error);
      // Fallback to localStorage if API fails
      if (isLoggedIn) {
        const localWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        const wishlistSet = new Set(localWishlist.map(item => String(item.product_id)));
        setWishlistItems(wishlistSet);
        setWishlistProducts(localWishlist);
        setWishlistCount(localWishlist.length);
      } else {
        setWishlistItems(new Set());
        setWishlistProducts([]);
        setWishlistCount(0);
      }
    }
  };

  // Initial load from localStorage immediately on mount
  useEffect(() => {
    const initialLoad = () => {
      const localWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
      if (localWishlist.length > 0) {
        // Normalize product IDs to ensure consistency
        const wishlistSet = new Set(localWishlist.map(item => String(item.product_id)));
        setWishlistItems(wishlistSet);
        setWishlistProducts(localWishlist);
        setWishlistCount(localWishlist.length);
        console.log('Initial load from localStorage:', localWishlist.length, 'items');
        console.log('Initial wishlist IDs:', Array.from(wishlistSet));
      }
    };
    
    initialLoad();
  }, []);

  // Load wishlist on component mount or when login status changes
  useEffect(() => {
    if (isLoggedIn !== null) {
      loadWishlistData();
    }
  }, [isLoggedIn, customerId]);

  // Add a method to force refresh wishlist data
  const refreshWishlist = async () => {
    await loadWishlistData();
  };

  // Add item to wishlist
  const addToWishlist = async (product) => {
    try {
      if (!isLoggedIn) {
        // Add to localStorage for non-logged-in users
        const localWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        
        // Check if item already exists
        const existingItem = localWishlist.find(item => item.product_id === product.product_id);
        if (existingItem) {
          return { success: false, message: "Item already in wishlist" };
        }

        localWishlist.push({
          product_id: product.product_id,
          ca_id: product.ca_id,
          name: product.name || product.product_name,
          price: product.price || product.pr_sprice || product.contact_lens_price || product.contactlens_sale_price,
          pr_sprice: product.pr_sprice || product.price,
          pr_image: product.pr_image || product.contactlens_images,
          pr_image_json: product.pr_image_json,
          slug: product.slug || product.contactlens_slug,
          // Store additional fields for better data consistency
          contactlens_name: product.contactlens_name,
          contactlens_slug: product.contactlens_slug,
          contactlens_sale_price: product.contactlens_sale_price,
          contactlens_images: product.contactlens_images,
          contact_lens_price: product.contact_lens_price,
          product_name: product.product_name,
          // Store image URLs and amount - prioritize pr_image_json
          image_url: product.pr_image_json || product.pr_image || product.contactlens_images,
          amount: product.amount || 1,
          dateAdded: new Date().toISOString()
        });

        localStorage.setItem("wishlist", JSON.stringify(localWishlist));
        
        // Update state immediately for UI responsiveness
        setWishlistItems(prev => new Set(prev).add(String(product.product_id)));
        setWishlistProducts(localWishlist);
        setWishlistCount(localWishlist.length);
        
        return { success: true, message: "Added to wishlist" };
      } else {
        // Add to API for logged-in users
        const response = await apiService.addProductToWishlist({
          product_id: product.product_id,
          customer_id: customerId,
          ca_id: product.ca_id
        });

        if (response.success) {
          // Update state immediately for UI responsiveness
          setWishlistItems(prev => new Set(prev).add(String(product.product_id)));
          setWishlistCount(prev => prev + 1);
          
          // Update products list immediately
          setWishlistProducts(prev => [...prev, {
            product_id: product.product_id,
            ca_id: product.ca_id,
            name: product.name || product.product_name,
            price: product.price || product.pr_sprice || product.contact_lens_price || product.contactlens_sale_price,
            pr_sprice: product.pr_sprice || product.price,
            pr_image: product.pr_image || product.contactlens_images,
            pr_image_json: product.pr_image_json,
            slug: product.slug || product.contactlens_slug,
            dateAdded: new Date().toISOString()
          }]);
          
          return { success: true, message: "Added to wishlist" };
        } else {
          return { success: false, message: "Failed to add to wishlist" };
        }
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return { success: false, message: "Error adding to wishlist" };
    }
  };

  // Remove item from wishlist
  const removeFromWishlist = async (productId) => {
    try {
      if (!isLoggedIn) {
        // Remove from localStorage for non-logged-in users
        const localWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        const updatedWishlist = localWishlist.filter(item => item.product_id !== productId);
        
        localStorage.setItem("wishlist", JSON.stringify(updatedWishlist));
        
        // Update state immediately for UI responsiveness
        setWishlistItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(String(productId));
          return newSet;
        });
        setWishlistProducts(updatedWishlist);
        setWishlistCount(updatedWishlist.length);
        
        return { success: true, message: "Removed from wishlist" };
      } else {
        // Remove from API for logged-in users
        const response = await apiService.removeProductFromWishlist({
          product_id: productId,
          customer_id: customerId
        });

        if (response.success) {
          // Update state immediately for UI responsiveness
          setWishlistItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(String(productId));
            return newSet;
          });
          setWishlistCount(prev => prev - 1);
          
          // Update products list immediately
          setWishlistProducts(prev => prev.filter(item => item.product_id !== productId));
          
          return { success: true, message: "Removed from wishlist" };
        } else {
          return { success: false, message: "Failed to remove from wishlist" };
        }
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return { success: false, message: "Error removing from wishlist" };
    }
  };

  // Check if item is in wishlist
  const isInWishlist = (productId) => {
    if (!productId) return false;
    
    // Convert to string for consistent comparison (since we store as strings)
    const productIdStr = String(productId);
    return wishlistItems.has(productIdStr);
  };

  // Toggle wishlist status
  const toggleWishlist = async (product) => {
    if (isInWishlist(product.product_id)) {
      return await removeFromWishlist(product.product_id);
    } else {
      return await addToWishlist(product);
    }
  };

  // Sync local wishlist to database on login
  const syncWishlistOnLogin = async () => {
    try {
      const localWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
      if (localWishlist.length > 0) {
        const updatedWishlist = localWishlist.map((item) => ({
          product_id: item.product_id,
          customer_id: customerId,
          ca_id: item.ca_id,
        }));
        
        await apiService.addMultipleItemsToWishlist(updatedWishlist);
        localStorage.removeItem("wishlist");
        
        // Reload wishlist data from server
        await loadWishlistData();
      }
    } catch (error) {
      console.error('Error syncing wishlist on login:', error);
    }
  };

  const value = {
    wishlistItems,
    wishlistProducts,
    wishlistCount,
    isLoggedIn,
    isLoading,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    toggleWishlist,
    loadWishlistData,
    refreshWishlist,
    syncWishlistOnLogin
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};